import React, { type SyntheticEvent, type MouseEventHandler } from 'react'
import Button from '@mui/joy/Button'
import Box from '@mui/joy/Box'
import Modal from '@mui/joy/Modal'
import ModalClose from '@mui/joy/ModalClose'
import ModalDialog from '@mui/joy/ModalDialog'
import FormLabel from '@mui/joy/FormLabel'
import FormControl from '@mui/joy/FormControl'
import { Trans, useTranslation } from 'react-i18next'
// @ts-ignore
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.js'
// @ts-ignore
import 'moment/dist/locale/cs'
// @ts-ignore
import 'moment/dist/locale/de'
// @ts-ignore
import 'moment/dist/locale/pl'
import type { Moment } from 'moment'
import IconButton from '@mui/joy/IconButton'
import SearchIcon from '@mui/icons-material/Search'
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded'
import Alert from '@mui/joy/Alert'

// React-dates picker
import 'react-dates-gte-react-17/initialize'
// @ts-ignore
import { DayPickerRangeController } from 'react-dates-gte-react-17'
import 'react-dates-gte-react-17/lib/css/_datepicker.css'
import '../styles/react-dates-overrides.scss'

import type {
    SearchType,
    TimeHighlight,
} from '../types'
import { ArrowForwardIcon } from './icons'
import TimePicker from './time-picker'
import {
    isInclusivelyAfterDay,
    getDayLimits,
    isSearchTimeRangeValid,
} from '../helpers'
import PlacesAutocomplete from './places-autocomplete'
import { END_DATE, START_DATE } from '../constants'
import type { SearchSubmissionType } from '../types'

export default function SearchModal({
    searchModalOpen,
    handleToggleSearchModalOpen,
    currentSearch,
    modalType,
    handleSearchReset,
    handleSelect,
    handleDateChange,
    handleFocusChange,
    garagesMap,
    reservationDays,
    reservationHours,
    selectedCETDateTime,
    currentCETDateTime,
    searchLink,
    setSearch,
    garageDetail,
    home,
    timeHighlight,
} : {
    searchModalOpen: boolean,
    handleToggleSearchModalOpen: () => void,
    currentSearch: SearchType,
    modalType: 'mobile' | 'desktop',
    handleSearchReset: MouseEventHandler<HTMLAnchorElement>,
    handleSelect: (event: SyntheticEvent | null, newValue: string | null, name : string) => void,
    handleDateChange: ({ startDate, endDate, }: {
        startDate: Moment | null,
        endDate: Moment | null,
    }, focusedInput: typeof START_DATE | typeof END_DATE) => void,
    handleFocusChange: (focusedInput: typeof START_DATE | typeof END_DATE) => void,
    garagesMap?: boolean | undefined,
    reservationDays: number,
    reservationHours: number,
    selectedCETDateTime: Moment,
    currentCETDateTime: Moment,
    searchLink?: string,
    setSearch?: (newSearch: SearchSubmissionType) => void,
    garageDetail?: boolean | undefined,
    home?: boolean | undefined,
    timeHighlight: TimeHighlight,
}) {

    const { t, i18n } = useTranslation()
    const userLang = i18n.language

    // Set moment locale from i18n, moment locale used by react-dates
    moment.locale(userLang)
    if (userLang === 'cs') {
        moment.updateLocale('cs', {
            months : [
                "leden", "únor", "březen", "duben", "květen", "červen", "červenec",
                "srpen", "září", "říjen", "listopad", "prosinec"
            ],
            weekdaysMin : [
                "Ne", "Po", "Út", "St", "Čt", "Pá", "So"
            ],
            weekdays : [
                "Neděle", "Pondělí", "Úterý", "Středa", "Čtvrtek", "Pátek", "Sobota"
            ]
        })
    }
    moment.tz.setDefault('Europe/Prague')

    const {
        maximumStayDays,
        maximumDaysInAdvance,
    } = getDayLimits()

    const isOutsideRange = (day: Moment): boolean => {
        // Check if day is today or later
        if (!(isInclusivelyAfterDay(day, moment()))) return true
        // Check if day isn't over 150 days from today
        if (isInclusivelyAfterDay(day, moment().add(maximumDaysInAdvance + 1, 'days'))) return true

        // endDate cannot be more than 30 nights from startDate
        if (currentSearch.startDate &&
            currentSearch.focusedInput === END_DATE &&
            isInclusivelyAfterDay(day, currentSearch.startDate.clone().add(maximumStayDays + 1, 'days'))
        ) return true

        return false
    }

    const reservationTimeRangeInvalid = !isSearchTimeRangeValid(currentSearch)
    const showInvalidTimeRange = reservationTimeRangeInvalid && Boolean(
        currentSearch.startDate && currentSearch.endDate && currentSearch.startTime && currentSearch.endTime,
    )
    const reservationInThePast = selectedCETDateTime < currentCETDateTime
    const searchDisabled = reservationTimeRangeInvalid || reservationInThePast

    if (modalType === 'mobile') {
        return (
            <Modal open={searchModalOpen} onClose={() => handleToggleSearchModalOpen()}>
                <ModalDialog
                    variant="outlined"
                    layout="fullscreen"
                    sx={{
                        backgroundColor: '#f7f7f7',
                        padding: '0',
                    }}
                >
                    <Box p={2.5} pb={1.5}>
                        {!garageDetail &&
                            <FormControl>
                                <FormLabel sx={{ fontWeight: 600 }}>{t('search.where')}</FormLabel>
                                <PlacesAutocomplete
                                    location={currentSearch.location}
                                    handleSelect={handleSelect}
                                />
                            </FormControl>
                        }
                        <Box display="flex" justifyContent={!garageDetail ? 'flex-start' : 'center'} pt={!garageDetail ? 2 : 0}>
                            <FormLabel sx={{ fontWeight: 600 }}>{t('search.selectDate')}</FormLabel>
                        </Box>
                    </Box>
                    {/* display="flex" is a fix for Safari height: 100% not working properly in PWA */}
                    <Box flexGrow={1} display="flex">
                        <DayPickerRangeController
                            startDate={currentSearch.startDate} // momentPropTypes.momentObj or null,
                            endDate={currentSearch.endDate} // momentPropTypes.momentObj or null,
                            onDatesChange={(e:{
                                startDate: Moment | null,
                                endDate: Moment | null,
                            }) => {
                                if (currentSearch.focusedInput === START_DATE) {
                                    handleDateChange(e, START_DATE)
                                } else {
                                    handleDateChange(e, END_DATE)
                                }
                            }} // PropTypes.func.isRequired,
                            focusedInput={currentSearch.focusedInput} // PropTypes.oneOf([START_DATE, END_DATE]) or null,
                            onFocusChange={(focusedInput: typeof START_DATE | typeof END_DATE) => handleFocusChange(focusedInput)} // PropTypes.func.isRequired,
                            // Keep to shift to a specific month when startDate selected, happens by default
                            initialVisibleMonth={() => moment()} // PropTypes.func or null,
                            orientation="verticalScrollable"
                            numberOfMonths={7}
                            verticalHeight={300}
                            noNavButtons
                            noBorder
                            renderKeyboardShortcutsButton={() => {}}
                            monthFormat="MMMM YYYY"
                            minimumNights={0}
                            isOutsideRange={(day: Moment) => isOutsideRange(day)}
                            minDate={moment()}
                            maxDate={moment().add(maximumDaysInAdvance + 1, 'days')}
                            daySize={46}
                        />
                    </Box>
                    <Box sx={{
                        fontSize: '0.875rem',
                        textAlign: 'center',
                        padding: '0.5rem',
                    }}>
                        {!currentSearch.startDate ? t('search.selectStart') :
                            !currentSearch.endDate ? 
                                <Box color="red">  
                                    {t('search.selectEnd')}
                                </Box>
                                : 
                                (reservationDays || reservationHours) ?
                                    <Box color={reservationTimeRangeInvalid ? 'red' : 'inherit'}>
                                        {t('search.reservationLength')+':'}
                                        <span>{reservationDays ? t('search.days', {count: reservationDays}) : ''}</span> 
                                        <span>{reservationHours ? t('search.hours', {count: reservationHours}) : ''}</span>
                                    </Box> 
                                    :
                                    <Box color='red'>{t('search.reservationLength')+': '+t('search.hours', {count: 0})}</Box>
                        }
                    </Box>
                    {reservationDays >= 7 &&
                        <Box sx={{
                            fontSize: '0.875rem',
                            textAlign: 'center',
                            padding: '0rem 0.5rem 0.5rem',
                        }}>
                            <Alert
                                variant="outlined"
                                color="warning"
                                sx={{
                                    backgroundColor: 'var(--joy-palette-warning-100)',
                                }}
                            ><p><Trans i18nKey="search.discountAvailable" /></p></Alert>
                        </Box>
                    }
                    {reservationInThePast &&
                        <Box sx={{
                            fontSize: '0.875rem',
                            textAlign: 'center',
                            padding: '0rem 0.5rem 0.5rem',
                        }}>
                            <Box color='red'>{t('search.startInThePast')}</Box>
                        </Box>
                    }
                    {showInvalidTimeRange &&
                        <Box sx={{
                            fontSize: '0.875rem',
                            textAlign: 'center',
                            padding: '0rem 0.5rem 0.5rem',
                        }}>
                            <Box color='red'>{t('search.invalidTimeRange')}</Box>
                        </Box>
                    }
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '1.3rem 1.25rem',
                        borderTop: '1px solid #E0E0E0',
                        gap: '1.3rem',
                    }}>
                        <Box display="flex" alignItems="center">
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                flex: '0 0 50%',
                                gap: '0.2rem',
                            }}>
                                <FormLabel sx={{ fontWeight: 600 }}>{t('search.arrivalAfter')}</FormLabel>
                                <Box sx={{ fontSize: '15px' }}>
                                    {currentSearch.startDate !== null ?
                                        moment(currentSearch.startDate).locale(userLang).format('dddd, DD. MMM') :
                                        '- -. - -. - - - -'
                                    }
                                </Box>
                            </Box>
                            <TimePicker
                                timeType='start'
                                handleSelect={handleSelect}
                                value={currentSearch.startTime}
                                timeHighlight={timeHighlight.startTime}
                                noLabel
                            />
                        </Box>
                        <Box display="flex" alignItems="center">
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                flex: '0 0 50%',
                                gap: '0.2rem',
                            }}>
                                <FormLabel sx={{ fontWeight: 600 }}>{t('search.departureBefore')}</FormLabel>
                                <Box sx={{ fontSize: '15px' }}>
                                    {currentSearch.endDate !== null ?
                                        moment(currentSearch.endDate).locale(userLang).format('dddd, DD. MMM') :
                                        '- -. - -. - - - -'
                                    }
                                </Box>
                            </Box>
                            <TimePicker
                                timeType='end'
                                handleSelect={handleSelect}
                                value={currentSearch.endTime}
                                timeHighlight={timeHighlight.endTime}
                                noLabel
                            />
                        </Box>
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: {
                            xs: '1rem 1.25rem 1.5rem',
                            sm: '1rem 1.25rem',
                        },
                        borderTop: '1px solid #E0E0E0',
                    }}>
                        <IconButton onClick={() => handleToggleSearchModalOpen()} sx={{
                            '&:hover': {
                                '& svg': {
                                    fill: 'var(--joy-palette-primary-900)',
                                },
                            },
                        }}>
                            <ArrowBackIosNewRoundedIcon sx={{
                                paddingRight: 0.3,
                                color: 'primary.800',
                            }}/>
                        </IconButton>
                        <Box sx={{
                            display: 'flex',
                            gap: '0.5rem',
                        }}>
                            <Button
                                variant="plain"
                                onClick={handleSearchReset}
                            >
                                {t('search.reset')}
                            </Button>
                            <Button
                                variant="solid"
                                component={searchLink ? 'a' : 'button'}
                                href={searchDisabled ? undefined : searchLink}
                                disabled={searchDisabled}
                                onClick={setSearch ? () => setSearch({
                                    startDate: currentSearch.startDate,
                                    startTime: currentSearch.startTime,
                                    endDate: currentSearch.endDate,
                                    endTime: currentSearch.endTime,
                                    location: currentSearch.location,
                                }) : undefined}
                                sx={{
                                    fontSize: '15px',
                                    padding: '0.5rem 1rem',
                                    borderRadius: '7px',
                                    // imitate disabled for link
                                    ...(!currentSearch.startDate ||
                                        !currentSearch.endDate ||
                                        reservationTimeRangeInvalid ||
                                        reservationInThePast) ? {
                                        pointerEvents: 'none',
                                        cursor: 'default',
                                        color: '#fff',
                                        backgroundColor: '#828282',
                                    } : {},
                                    // legacy application stylesheet override
                                    '&:hover': {
                                        color: 'secondary.solidBg',
                                    },
                                }}
                                startDecorator={
                                    <SearchIcon
                                        sx={{
                                            color: 'inherit',
                                            fontSize: '22px',
                                        }}
                                    />
                                }
                            >
                                {garageDetail ? t('search.edit') : t('search.search')}
                            </Button>
                        </Box>
                    </Box>
                </ModalDialog>
            </Modal>
        )
    }

    return (
        <Modal open={searchModalOpen} onClose={() => handleToggleSearchModalOpen()}>
            <ModalDialog
                variant="outlined"
                sx={{
                    backgroundColor: '#f7f7f7',
                    padding: '0',
                    borderRadius: '0.25rem',
                    border: 'none',
                    overflow: 'hidden',
                    maxWidth: '100vw',
                    ...garagesMap ? {
                        top: reservationDays >= 7 ? '468px' : '442px',
                    } : {},
                }}
            >
                {home &&
                    <ModalClose variant="plain"/>
                }
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: 'primary.solidBg',
                    p: home ? '1rem 1.25rem 0.75rem' : '0.75rem',
                }}>
                    <Box sx={{
                        fontSize: '0.825rem',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        color: 'white',
                        marginBottom: '0.25rem',
                    }}>
                        {t('search.reservationDate')}
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        color: 'secondary.solidBg',
                        justifyContent: 'start',
                        fontWeight: 500,
                        fontSize: '1.125rem',
                    }}>
                        <Box>{currentSearch.startDate !== null ?
                            <Box display="inline">
                                <Box display="inline" textTransform="capitalize">
                                    {moment(currentSearch.startDate).format('dddd, ')}
                                </Box>
                                {moment(currentSearch.startDate).format('D. MMMM')}
                            </Box> :
                            '- -. - -. - - - -'}, {currentSearch.startTime ?? '--:--'}
                        </Box>
                        <Box px={2}>
                            <ArrowForwardIcon color="#ffc400"/>
                        </Box>
                        <Box>{currentSearch.endDate !== null ?
                            <Box display="inline">
                                <Box display="inline" textTransform="capitalize">
                                    {moment(currentSearch.endDate).format('dddd, ')}
                                </Box>
                                {moment(currentSearch.endDate).format('D. MMMM')}
                            </Box> :
                            '- -. - -. - - - -'}, {currentSearch.endTime ?? '--:--'}
                        </Box>
                    </Box>
                </Box>
                {!garageDetail &&
                    <Box pt={2.5} pb={1.5} px={3}>
                        <FormControl>
                            <FormLabel>{t('search.where')}</FormLabel>
                            <PlacesAutocomplete
                                location={currentSearch.location}
                                handleSelect={handleSelect}
                            />
                        </FormControl>
                    </Box>
                }
                <Box display="flex">
                    <Box sx={{
                        padding: '1.5rem',
                        minWidth: 160,
                    }}>
                        <TimePicker
                            timeType='start'
                            handleSelect={handleSelect}
                            value={currentSearch.startTime}
                            timeHighlight={timeHighlight.startTime}
                            desktop
                        />
                    </Box>
                    <Box minHeight={340}>
                        <DayPickerRangeController
                            startDate={currentSearch.startDate} // Required
                            endDate={currentSearch.endDate} // Required
                            onDatesChange={(e:{
                                startDate: Moment | null,
                                endDate: Moment | null,
                            })  => {
                                if (currentSearch.focusedInput === START_DATE) {
                                    handleDateChange(e, START_DATE)
                                } else {
                                    handleDateChange(e, END_DATE)
                                }
                            }} // Required
                            focusedInput={currentSearch.focusedInput} // Required
                            onFocusChange={(focusedInput: typeof START_DATE | typeof END_DATE)  => handleFocusChange(focusedInput)} // Required
                            numberOfMonths={2}
                            verticalHeight={400}
                            noBorder
                            renderKeyboardShortcutsButton={() => {}}
                            monthFormat="MMMM YYYY"
                            minimumNights={0}
                            isOutsideRange={(day: Moment) => isOutsideRange(day)}
                            minDate={moment()}
                            maxDate={moment().add(maximumDaysInAdvance + 1, 'days')}
                            transitionDuration={0}
                        />
                    </Box>
                    <Box sx={{
                        padding: '1.5rem',
                        minWidth: 160,
                    }}>
                        <TimePicker
                            timeType='end'
                            handleSelect={handleSelect}
                            value={currentSearch.endTime}
                            timeHighlight={timeHighlight.endTime}
                            desktop
                        />
                    </Box>
                </Box>
                {reservationDays >= 7 &&
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        p: 0.5,
                        pb: 1,
                    }}>
                        <Alert
                            variant="outlined"
                            color="warning"
                            sx={{
                                backgroundColor: 'var(--joy-palette-warning-100)',
                                py: 1,
                            }}
                        ><p><Trans i18nKey="search.discountAvailable" /></p></Alert>
                    </Box>
                }
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.75rem 1rem',
                    borderTop: '1px solid #E0E0E0'
                }}>
                    <Button
                        variant="plain"
                        onClick={handleSearchReset}
                    >
                        {t('search.reset')}
                    </Button>
                    <Box display="flex">
                        <Box sx={{
                            fontSize: '0.875rem',
                            textAlign: 'right',
                            padding: '0 1rem 0 0.5rem',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                        }}>
                            {!currentSearch.startDate ? t('search.selectStart') :
                                !currentSearch.endDate ? 
                                    <Box color="red">  
                                        {t('search.selectEnd')}
                                    </Box>    
                                    : 
                                    (reservationDays || reservationHours) ?
                                        <Box color={reservationTimeRangeInvalid ? 'red' : 'inherit'}>
                                            {t('search.reservationLength')+':'}
                                            <span>{reservationDays ? t('search.days', {count: reservationDays}) : ''}</span> 
                                            <span>{reservationHours ? t('search.hours', {count: reservationHours}) : ''}</span>
                                        </Box> 
                                        :
                                        <Box color='red'>{t('search.reservationLength')+': '+t('search.hours', {count: 0})}</Box>
                            }
                            {reservationInThePast &&
                                <Box color='red'>{t('search.startInThePast')}</Box>
                            }
                            {showInvalidTimeRange &&
                                <Box color='red'>{t('search.invalidTimeRange')}</Box>
                            }
                        </Box>
                        <Button
                            variant="solid"
                            component={searchLink ? 'a' : 'button'}
                            href={searchDisabled ? undefined : searchLink}
                            disabled={searchDisabled}
                            onClick={setSearch ? () => setSearch({
                                startDate: currentSearch.startDate,
                                startTime: currentSearch.startTime,
                                endDate: currentSearch.endDate,
                                endTime: currentSearch.endTime,
                                location: currentSearch.location,
                            }) : undefined}
                            sx={{
                                paddingLeft: '1.5rem',
                                paddingRight: '1.5rem',
                                // imitate disabled for link
                                ...(!currentSearch.startDate ||
                                    !currentSearch.endDate ||
                                    reservationTimeRangeInvalid ||
                                    reservationInThePast) ? {
                                    pointerEvents: 'none',
                                    cursor: 'default',
                                    color: '#fff',
                                    backgroundColor: '#828282',
                                } : {},
                                // legacy application stylesheet override
                                '&:hover': {
                                    color: 'secondary.solidBg',
                                }
                            }}
                        >
                            {garageDetail ? t('search.edit') : t('search.search')}
                        </Button>
                    </Box>
                </Box>
            </ModalDialog>
        </Modal>
    )
}
